/**
 * Registre des modèles de mail.
 *
 * **Tout mail que l'application compose doit avoir son modèle ici**, et donc
 * être modifiable depuis Réglages. Les textes vivaient auparavant dans quatre
 * fichiers, sous quatre formes — une table de fragments pour la commande
 * fournisseur, une autre pour le mail de tracking, deux champs plats pour la
 * relance transporteur, une table `{objet, corps}` pour la relance
 * fournisseur. Trois d'entre eux n'étaient donc modifiables qu'en déployant,
 * et rien ne disait lesquels.
 *
 * Une seule forme désormais : `{objet, corps}` en texte brut, avec des
 * variables `{{…}}`. C'est celle que l'écran sait déjà présenter, et celle que
 * l'utilisateur relit avant envoi.
 *
 * **Le repli est champ par champ.** Vider l'objet dans Réglages y remet celui
 * du code sans toucher au corps — ce qui donne un moyen simple de revenir en
 * arrière sur une seule ligne, sans avoir à la retrouver.
 */
/** Les mails que l'application sait composer. */
export const TYPES_MAIL = [
    'commande_fournisseur',
    'commande_repassee',
    'relance_fournisseur',
    'relance_tracking',
    'relance_transporteur',
    'expedition_client',
    'tracking_client',
    'relance_reglement',
    'enlevement_hiolle',
];
const V = (nom, description) => ({ nom, description });
/** Variables communes à tous les mails liés à une commande. */
const COMMUNES = [
    V('{{ref}}', 'Référence de la commande'),
    V('{{client}}', 'Nom du client'),
    V('{{ville}}', 'Ville de livraison'),
];
const ARTICLES = V('{{articles}}', 'Lignes produit, une par ligne, en désignation fournisseur');
const ADRESSE = V('{{adresse}}', 'Bloc « adresse de livraison » complet');
export const MODELES_MAIL = {
    commande_fournisseur: {
        libelle: 'Commande au fournisseur',
        aQuoiCaSert: 'Transmet une commande neuve au fournisseur. Composé, relu à l’écran, puis envoyé depuis contact@.',
        langues: ['fr', 'en'],
        variables: [...COMMUNES, V('{{fournisseur}}', 'Société destinataire'), ARTICLES, ADRESSE],
        defaut: {
            fr: {
                objet: 'Nouvelle Commande {{ref}} - {{client}}',
                corps: [
                    'Bonjour,',
                    'Nous souhaitons passer une commande pour les produits suivants :',
                    '',
                    '{{articles}}',
                    '',
                    '{{adresse}}',
                    '',
                    'Nous restons disponibles si besoin,',
                    'Merci à vous',
                ].join('\n'),
            },
            en: {
                objet: 'New Order {{ref}} - {{client}}',
                corps: [
                    'Hello,',
                    'We would like to place an order for the following products:',
                    '',
                    '{{articles}}',
                    '',
                    '{{adresse}}',
                    '',
                    'We remain available if needed,',
                    'Thank you',
                ].join('\n'),
            },
        },
    },
    commande_repassee: {
        libelle: 'Commande repassée au fournisseur',
        aQuoiCaSert: 'Renvoie une commande que le fournisseur ne retrouve pas. Dit trois choses que le mail '
            + 'initial ne dit pas : que c’est un renvoi, pourquoi, et qu’il y a urgence — sans quoi '
            + 'le fournisseur ne peut pas savoir s’il doit livrer une fois ou deux.',
        langues: ['fr', 'en'],
        variables: [...COMMUNES, V('{{fournisseur}}', 'Société destinataire'), ARTICLES, ADRESSE],
        defaut: {
            fr: {
                objet: 'RELANCE — Commande {{ref}} - {{client}} (sans trace chez vous)',
                corps: [
                    'Bonjour,',
                    'Nous revenons vers vous au sujet de notre commande {{ref}}, que nous vous avons déjà',
                    "transmise et qui n'apparaît pas dans votre système.",
                    '',
                    "Il ne s'agit donc pas d'une nouvelle commande : nous vous en retransmettons ci-dessous",
                    "tous les éléments, à l'identique.",
                    '',
                    '{{articles}}',
                    '',
                    '{{adresse}}',
                    '',
                    'Merci de nous confirmer sa bonne réception et de la traiter en priorité : le délai qui',
                    "court maintenant s'ajoute à celui déjà écoulé depuis notre première demande, et notre",
                    'client attend toujours.',
                    '',
                    'Nous restons disponibles si besoin,',
                    'Merci à vous',
                ].join('\n'),
            },
            en: {
                objet: 'FOLLOW-UP — Order {{ref}} - {{client}} (no trace on your side)',
                corps: [
                    'Hello,',
                    'We are contacting you again about our order {{ref}}, which we already sent you and',
                    'which does not appear in your system.',
                    '',
                    'This is therefore NOT a new order: we are sending you all of its details again below,',
                    'unchanged.',
                    '',
                    '{{articles}}',
                    '',
                    '{{adresse}}',
                    '',
                    'Please confirm that you have received it and give it priority: the time it takes from',
                    'now adds to the delay already incurred since our first request, and our customer is',
                    'still waiting.',
                    '',
                    'We remain available if needed,',
                    'Thank you',
                ].join('\n'),
            },
        },
    },
    relance_fournisseur: {
        libelle: 'Relance — date d’expédition',
        aQuoiCaSert: 'Réclame une date d’expédition ferme. Ne récite pas la commande : on écrit à quelqu’un '
            + 'qui l’a déjà.',
        langues: ['fr', 'en'],
        variables: [
            ...COMMUNES,
            V('{{fournisseur}}', 'Société relancée'),
            V('{{transmise}}', 'Date d’envoi au fournisseur'),
            V('{{attente}}', 'Jours écoulés depuis la transmission'),
            V('{{reception}}', 'Date de réception de la commande'),
            V('{{note}}', 'Note portée sur la commande'),
            ARTICLES,
            ADRESSE,
        ],
        defaut: {
            fr: {
                objet: 'Relance commande {{ref}} — dans l’attente d’une date d’expédition',
                corps: [
                    'Bonjour,',
                    '',
                    'Nous vous avons transmis la commande {{ref}} le {{transmise}} et sommes à ce jour sans date',
                    'd’expédition ({{attente}} jours).',
                    '',
                    'Pouvez-vous nous confirmer la bonne réception de cette commande et nous communiquer une date',
                    'd’expédition ferme ? Si vous ne la retrouvez pas dans votre système, merci de nous le dire :',
                    'nous vous la repasserons en intégralité.',
                    '',
                    'Merci d’avance.',
                    '',
                    'Cordialement,',
                ].join('\n'),
            },
            en: {
                objet: 'Order {{ref}} — still awaiting a dispatch date',
                corps: [
                    'Hello,',
                    '',
                    'We sent you order {{ref}} on {{transmise}} and have not received a dispatch date so far',
                    '({{attente}} days).',
                    '',
                    'Could you please confirm that you have received this order and give us a firm dispatch date?',
                    'If you cannot find it in your system, please let us know and we will send it again in full.',
                    '',
                    'Thank you in advance.',
                    '',
                    'Best regards,',
                ].join('\n'),
            },
        },
    },
    relance_tracking: {
        libelle: 'Relance — numéro de suivi',
        aQuoiCaSert: 'Réclame le numéro de suivi d’une commande DÉJÀ partie, chez un fournisseur qui expédie '
            + 'lui-même (R-TEK). Il n’y a alors aucun transporteur à qui le demander.',
        langues: ['fr', 'en'],
        variables: [
            ...COMMUNES,
            V('{{fournisseur}}', 'Société relancée'),
            V('{{expediee}}', 'Date d’expédition'),
            V('{{depuis}}', 'Jours écoulés depuis le départ'),
            ARTICLES,
            ADRESSE,
        ],
        defaut: {
            fr: {
                objet: 'Commande {{ref}} — numéro de suivi manquant',
                corps: [
                    'Bonjour,',
                    '',
                    'La commande {{ref}} nous est indiquée comme expédiée le {{expediee}} ({{depuis}} jours), mais',
                    'nous n’avons pas reçu de numéro de suivi.',
                    '',
                    'Pouvez-vous nous communiquer le numéro de suivi ainsi que le transporteur retenu ? Sans ces',
                    'deux éléments, nous ne pouvons ni suivre l’envoi ni informer notre client de la date de',
                    'livraison.',
                    '',
                    'Merci d’avance.',
                    '',
                    'Cordialement,',
                ].join('\n'),
            },
            en: {
                objet: 'Order {{ref}} — tracking number missing',
                corps: [
                    'Hello,',
                    '',
                    'Order {{ref}} is recorded as dispatched on {{expediee}} ({{depuis}} days ago), but we have not',
                    'received a tracking number.',
                    '',
                    'Could you please send us the tracking number along with the carrier used? Without both, we',
                    'cannot follow the shipment or tell our customer when to expect delivery.',
                    '',
                    'Thank you in advance.',
                    '',
                    'Best regards,',
                ].join('\n'),
            },
        },
    },
    relance_transporteur: {
        libelle: 'Relance — livraison bloquée',
        aQuoiCaSert: 'Réclame une livraison qui traîne. Adressée au FOURNISSEUR, pas au transporteur : c’est '
            + 'lui le donneur d’ordre, et le seul à pouvoir faire bouger son transporteur. Exception : '
            + 'un envoi HIOLLE parti se réclame directement au transporteur.',
        langues: ['fr'],
        variables: [
            ...COMMUNES,
            V('{{tracking}}', 'Numéro de suivi'),
            V('{{transporteur}}', 'Transporteur'),
            V('{{expedition}}', 'Date de départ'),
            V('{{prevue}}', 'Livraison estimée'),
            V('{{retard}}', 'Jours de retard'),
            V('{{statut}}', 'Dernier statut relevé'),
        ],
        defaut: {
            fr: {
                objet: 'Livraison bloquée — commande {{ref}} / suivi {{tracking}}',
                corps: [
                    'Bonjour,',
                    '',
                    'Votre expédition de notre commande {{ref}}, partie le {{expedition}} par {{transporteur}},',
                    'n’est toujours pas livrée à ce jour.',
                    '',
                    'Numéro de suivi : {{tracking}}',
                    'Destinataire : {{client}}, {{ville}}',
                    'Livraison estimée : {{prevue}} (retard constaté : {{retard}} jours)',
                    'Dernier statut remonté : {{statut}}',
                    '',
                    'Pouvez-vous intervenir auprès de {{transporteur}} pour obtenir la position exacte du colis',
                    'et une date de livraison ferme ?',
                    'En cas d’avarie ou de perte, merci de nous transmettre le constat correspondant.',
                    '',
                    'Cordialement,',
                ].join('\n'),
            },
        },
    },
    expedition_client: {
        libelle: 'Client — annonce du départ',
        aQuoiCaSert: 'Prévient le client que sa commande part. Composé dans SA langue, relu à l’écran, puis '
            + 'envoyé depuis contact@ — sauf date relevée sur le portail Fortemix : il part alors '
            + 'seul, sans relecture.',
        langues: ['fr', 'en', 'it', 'es', 'de'],
        variables: [...COMMUNES, V('{{date}}', 'Date d’expédition, en toutes lettres')],
        defaut: {
            fr: {
                objet: 'Expédition Commande {{ref}}',
                corps: [
                    'Bonjour,',
                    '',
                    'Nous vous informons que votre commande a été planifiée en production et sera expédiée le {{date}}.',
                    'Il faudra compter par la suite 3 à 5 jours pour la livraison.',
                    'Nous ne manquerons pas de vous faire parvenir le lien de suivi.',
                    '',
                    'Je reste à votre disposition pour tout développement à ce sujet.',
                    '',
                    'Belle journée à vous,',
                    'Cordialement',
                ].join('\n'),
            },
            en: {
                objet: 'Order shipment {{ref}}',
                corps: [
                    'Dear Customer,',
                    '',
                    'We are pleased to inform you that your order has been scheduled for production and will be shipped on {{date}}.',
                    'Please allow 3 to 5 business days for delivery following the shipment.',
                    'We will make sure to provide you with the tracking link as soon as it becomes available.',
                    '',
                    'I remain at your disposal should you require any further information.',
                    '',
                    'Best regards',
                ].join('\n'),
            },
            it: {
                objet: 'Spedizione ordine {{ref}}',
                corps: [
                    'Buongiorno,',
                    '',
                    'La informiamo che il suo ordine è stato pianificato in produzione e sarà spedito il {{date}}.',
                    'Occorrerà poi calcolare dai 3 ai 5 giorni per la consegna.',
                    'Non mancheremo di inviarle il link per seguire la spedizione.',
                    '',
                    'Resto a sua disposizione per qualsiasi chiarimento.',
                    '',
                    'Cordiali saluti',
                ].join('\n'),
            },
            es: {
                objet: 'Envío del pedido {{ref}}',
                corps: [
                    'Buenos días,',
                    '',
                    'Le informamos de que su pedido ha sido planificado en producción y se enviará el {{date}}.',
                    'A continuación habrá que contar de 3 a 5 días para la entrega.',
                    'No dejaremos de enviarle el enlace de seguimiento.',
                    '',
                    'Quedo a su disposición para cualquier aclaración.',
                    '',
                    'Un cordial saludo',
                ].join('\n'),
            },
            de: {
                objet: 'Versand Ihrer Bestellung {{ref}}',
                corps: [
                    'Guten Tag,',
                    '',
                    'Wir teilen Ihnen mit, dass Ihre Bestellung in die Produktion eingeplant wurde und am {{date}} versandt wird.',
                    'Danach sind 3 bis 5 Tage für die Lieferung einzuplanen.',
                    'Den Sendungsverfolgungslink senden wir Ihnen selbstverständlich zu.',
                    '',
                    'Für Rückfragen stehe ich Ihnen gerne zur Verfügung.',
                    '',
                    'Mit freundlichen Grüßen',
                ].join('\n'),
            },
        },
    },
    tracking_client: {
        libelle: 'Client — numéro de suivi',
        aQuoiCaSert: 'Communique le suivi au client. Le bloc {{suivi}} est composé par l’application : il '
            + 'porte le lien quand il existe, le numéro sinon, et le transporteur — cette logique-là '
            + 'ne se règle pas dans un texte. Un tracking relevé sur le portail Fortemix l’envoie '
            + 'seul, sans relecture.',
        langues: ['fr', 'en', 'it', 'es', 'de'],
        variables: [
            ...COMMUNES,
            V('{{suivi}}', 'Lien ou numéro de suivi, transporteur compris — composé par l’application'),
        ],
        defaut: {
            fr: {
                objet: 'Expédition commande {{ref}}',
                corps: [
                    'Bonjour,',
                    '',
                    'Votre commande est expédiée.',
                    '{{suivi}}',
                    '',
                    'Restant à votre disposition pour tout complément d’information.',
                    '',
                    'Cordialement',
                ].join('\n'),
            },
            en: {
                objet: 'Tracking order {{ref}}',
                corps: [
                    'Hello,',
                    '',
                    'Your order has been shipped.',
                    '{{suivi}}',
                    '',
                    'We remain at your disposal for any further information.',
                    '',
                    'Best regards',
                ].join('\n'),
            },
            it: {
                objet: 'Spedizione ordine {{ref}}',
                corps: [
                    'Buongiorno,',
                    '',
                    'Il suo ordine è stato spedito.',
                    '{{suivi}}',
                    '',
                    'Restiamo a sua disposizione per ogni ulteriore informazione.',
                    '',
                    'Cordiali saluti',
                ].join('\n'),
            },
            es: {
                objet: 'Envío del pedido {{ref}}',
                corps: [
                    'Buenos días,',
                    '',
                    'Su pedido ha sido enviado.',
                    '{{suivi}}',
                    '',
                    'Quedamos a su disposición para cualquier información adicional.',
                    '',
                    'Un cordial saludo',
                ].join('\n'),
            },
            de: {
                objet: 'Versand Ihrer Bestellung {{ref}}',
                corps: [
                    'Guten Tag,',
                    '',
                    'Ihre Bestellung wurde versandt.',
                    '{{suivi}}',
                    '',
                    'Für weitere Auskünfte stehen wir Ihnen gerne zur Verfügung.',
                    '',
                    'Mit freundlichen Grüßen',
                ].join('\n'),
            },
        },
    },
    relance_reglement: {
        libelle: 'Client — relance de règlement',
        aQuoiCaSert: 'Rappelle au client que sa facture n’est pas réglée. Composé depuis la case « Règlement » '
            + 'de la fiche, relu à l’écran, puis envoyé depuis contact@ — il ne part qu’au clic sur « Envoyer ».',
        langues: ['fr', 'en', 'it', 'es', 'de'],
        variables: [
            ...COMMUNES,
            V('{{montant}}', 'Reste dû relevé chez Sellsy, à défaut le montant de la commande'),
        ],
        defaut: {
            fr: {
                objet: 'Règlement de votre commande {{ref}}',
                corps: [
                    'Bonjour,',
                    '',
                    'Sauf erreur de notre part, le règlement de votre commande {{ref}}, d’un montant de {{montant}}, ne nous est pas encore parvenu.',
                    'Votre commande sera transmise en production dès sa réception.',
                    '',
                    'Si le paiement a été effectué entre-temps, merci de ne pas tenir compte de ce message.',
                    '',
                    'Restant à votre disposition pour tout complément d’information.',
                    '',
                    'Cordialement',
                ].join('\n'),
            },
            en: {
                objet: 'Payment for your order {{ref}}',
                corps: [
                    'Hello,',
                    '',
                    'Unless we are mistaken, we have not yet received payment for your order {{ref}}, amounting to {{montant}}.',
                    'Your order will be sent to production as soon as payment is received.',
                    '',
                    'If payment has been made in the meantime, please disregard this message.',
                    '',
                    'We remain at your disposal for any further information.',
                    '',
                    'Best regards',
                ].join('\n'),
            },
            it: {
                objet: 'Pagamento del suo ordine {{ref}}',
                corps: [
                    'Buongiorno,',
                    '',
                    'Salvo errore da parte nostra, non abbiamo ancora ricevuto il pagamento del suo ordine {{ref}}, per un importo di {{montant}}.',
                    'Il suo ordine sarà messo in produzione non appena riceveremo il pagamento.',
                    '',
                    'Se il pagamento è già stato effettuato, la preghiamo di non tenere conto di questo messaggio.',
                    '',
                    'Restiamo a sua disposizione per ogni ulteriore informazione.',
                    '',
                    'Cordiali saluti',
                ].join('\n'),
            },
            es: {
                objet: 'Pago de su pedido {{ref}}',
                corps: [
                    'Buenos días,',
                    '',
                    'Salvo error por nuestra parte, aún no hemos recibido el pago de su pedido {{ref}}, por un importe de {{montant}}.',
                    'Su pedido pasará a producción en cuanto recibamos el pago.',
                    '',
                    'Si ya ha realizado el pago, le rogamos que no tenga en cuenta este mensaje.',
                    '',
                    'Quedamos a su disposición para cualquier información adicional.',
                    '',
                    'Un cordial saludo',
                ].join('\n'),
            },
            de: {
                objet: 'Zahlung Ihrer Bestellung {{ref}}',
                corps: [
                    'Guten Tag,',
                    '',
                    'Sofern wir uns nicht irren, ist die Zahlung für Ihre Bestellung {{ref}} über {{montant}} bei uns noch nicht eingegangen.',
                    'Ihre Bestellung geht in Produktion, sobald die Zahlung eingegangen ist.',
                    '',
                    'Sollte die Zahlung inzwischen erfolgt sein, betrachten Sie diese Nachricht bitte als gegenstandslos.',
                    '',
                    'Für weitere Auskünfte stehen wir Ihnen gerne zur Verfügung.',
                    '',
                    'Mit freundlichen Grüßen',
                ].join('\n'),
            },
        },
    },
    enlevement_hiolle: {
        libelle: 'HIOLLE — enlèvement DHL réservé',
        aQuoiCaSert: 'Répond à HIOLLE dans le fil de la commande, une fois l’enlèvement réservé chez DHL, et '
            + 'joint les étiquettes à coller sur les palettes.',
        langues: ['fr'],
        variables: [
            V('{{ref}}', 'Référence de la commande'),
            V('{{enlevement}}', 'Jour d’enlèvement'),
            V('{{palettes}}', 'Nombre de palettes'),
            V('{{poids}}', 'Poids total en kg'),
            V('{{volume}}', 'Volume total en m³'),
            V('{{plancher}}', 'Mètres de plancher'),
            V('{{envoi}}', 'Référence DHL de l’envoi'),
            V('{{documents}}', 'Phrase sur les pièces jointes, composée par l’application'),
        ],
        defaut: {
            fr: {
                objet: 'Enlèvement DHL {{ref}} — {{palettes}} palette(s), le {{enlevement}}',
                corps: [
                    'Bonjour,',
                    '',
                    'L’enlèvement est commandé chez DHL pour la commande {{ref}}.',
                    '',
                    '- Enlèvement prévu le {{enlevement}}',
                    '- {{palettes}} palette(s) · {{poids}} kg · {{volume}} m³ · {{plancher}} m de plancher',
                    '- Référence DHL : {{envoi}}',
                    '',
                    '{{documents}}',
                    '',
                    'Merci d’avance,',
                ].join('\n'),
            },
        },
    },
};
/**
 * Modèle effectif d'un mail, réglages d'abord, code en repli.
 *
 * Le repli joue **champ par champ** : un objet vidé dans Réglages reprend
 * celui du code sans que le corps bouge. C'est ce qui permet de revenir en
 * arrière sur une seule ligne sans avoir à la retrouver.
 *
 * Une langue absente retombe sur la première déclarée — le français partout —
 * plutôt que de laisser un modèle vide partir chez un client.
 */
export function modeleMail(reglages, type, langue) {
    const description = MODELES_MAIL[type];
    const replis = description.langues[0];
    const defaut = description.defaut[langue] ?? description.defaut[replis];
    const enregistres = reglages?.modelesMail?.[type];
    const choisi = enregistres?.[langue] ?? enregistres?.[replis];
    return {
        objet: choisi?.objet?.trim() ? choisi.objet : defaut.objet,
        corps: choisi?.corps?.trim() ? choisi.corps : defaut.corps,
    };
}
/** Table complète des modèles par défaut, pour le seed et les Réglages. */
export function modelesMailParDefaut() {
    const sortie = {};
    for (const type of TYPES_MAIL) {
        sortie[type] = {};
        for (const langue of MODELES_MAIL[type].langues) {
            const m = MODELES_MAIL[type].defaut[langue];
            if (m)
                sortie[type][langue] = { ...m };
        }
    }
    return sortie;
}
//# sourceMappingURL=modeles-mail.js.map